// The uPlot instance and its lifecycle: creation, reuse, the visible window
// across redraws, range presets, resize and theme repaint. main.ts drawChart
// hands it the lines (lib/chartmodel); which window to show is decided in
// lib/xwindow. Not in lib/: this is the one module that drives the canvas.
import uPlot from "uplot";
import { esc, fmtDec, fmtTick, periodLabel } from "./lib/format";
import { monthsShort } from "./lib/locale";
import { onThemeChange } from "./lib/theme";
import { periodMidShiftSec } from "./lib/transforms";
import { presetWindow, windowAfterDraw, type XScale, type XWindow } from "./lib/xwindow";

export const CHART_H = 450;  // chart canvas height (one source of truth: drawLines + resize)

/** The lines as drawn: what the tooltip and the y axis read. */
export interface Drawn {
  dates: string[];
  series: { label: string; color: string; dash?: number[]; values: (number | null)[] }[];
  freq: string | null | undefined;
  /** Ratio-of-GDP levels: axis and tooltip as a percentage. */
  pct: boolean;
}

/** What main.ts does when the visitor zooms. */
export interface ChartEvents {
  /** A drag-select on the canvas. */
  onUserZoom: () => void;
  /** Any change of the x scale, including the ones drawLines makes. */
  onXScale: () => void;
}

let plot: uPlot | null = null;
let shift = 0;   // display seconds = raw + shift (the period midpoint)
let drawn: Drawn | null = null;
let events: ChartEvents = { onUserZoom: () => {}, onXScale: () => {} };

export function onChartEvents(e: ChartEvents) { events = e; }
export const hasChart = (): boolean => !!plot;
/** The current x scale, null without a plot. */
export const chartScale = (): XScale | null => plot ? { min: plot.scales.x?.min, max: plot.scales.x?.max } : null;
export const chartShift = (): number => shift;

/** Tear the plot down, so the next draw builds a fresh one. */
export function clearChart() {
  if (plot) { plot.destroy(); plot = null; }
}

/** Show a range preset ("all", "1", "5", ...) on the drawn lines. "custom" and
 * no lines leave the window as it is. Zoom the x-axis to the last N years:
 * range presets are the mobile-friendly way to focus a long series (touch
 * drag-zoom is unreliable on a canvas). */
export function showRange(dates: string[] | null | undefined, range: string | null | undefined) {
  if (!plot || !dates || !dates.length) return;
  const w = presetWindow(dates, shift, range);
  if (w) plot.setScale("x", w);
}

// Chart chrome (axis ink, gridlines) comes from the CSS custom properties, so
// light/dark lives entirely in app.css. uPlot re-evaluates a function-valued
// stroke on every draw, which is what makes a theme flip a plain redraw().
const cssInk = (name: string, fallback: string) => () =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
const axisStroke = cssInk("--chart-axis", "#A79D90");
const gridStroke = cssInk("--chart-grid", "#F0F2F4");

// #14: period-aware x tick labels for quarterly/monthly (derive the period from
// the UNSHIFTED instant, since the tick now sits at the midpoint). Other
// frequencies keep uPlot's default time formatting.
// Below this plot width, x labels drop to the year alone. A "Dec 1993" label is
// ~54 px and uPlot spaces ticks 50 px apart, so on a phone they run together
// (`Dec 1993Dec 2003Dec 2013`). Thinning the ticks instead would leave one or
// two labels on a 40-year span — shortening them keeps every tick labelled, and
// on a span that wide the month was never the point.
const TERSE_TICKS_BELOW = 420;
function fmtPeriodTick(freq: string) {
  const sh = periodMidShiftSec(freq);
  return (u: uPlot, splits: number[]) => splits.map(s => {
    const d = new Date((s - sh) * 1000), y = d.getUTCFullYear(), mo = d.getUTCMonth();
    if (u.width < TERSE_TICKS_BELOW) return String(y);   // narrow: year only, or they collide
    if (freq === "quarterly") return `Q${Math.floor(mo / 3) + 1} ${y}`;
    if (freq === "monthly") return `${monthsShort()[mo]} ${y}`;
    return String(y);
  });
}

// Hover tooltip: date + each visible series' exact value (with its colour). A
// small uPlot plugin — reads u.cursor.idx and the plotted u.data, so the values
// shown are exactly what's on screen (level / transform / index).
function tipDate(idx: number): string {
  const iso = drawn && drawn.dates[idx]; if (!iso) return "";
  return periodLabel(iso, drawn && drawn.freq);
}
const tipVal = (v: number) => drawn && drawn.pct ? fmtDec(v * 100, 1) + "%" : fmtDec(v, 2);
function tooltipPlugin(): uPlot.Plugin {
  let tip: HTMLDivElement | null = null;
  return { hooks: {
    init: u => { tip = document.createElement("div"); tip.className = "chart-tip"; tip.style.display = "none"; u.over.appendChild(tip); },
    setCursor: u => {
      if (!tip) return;
      const { idx, left, top } = u.cursor;
      if (idx == null || left == null || top == null || left < 0 || top < 0) { tip.style.display = "none"; return; }
      let rows = "";
      for (let s = 1; s < u.series.length; s++) {
        const v = u.data[s]?.[idx]; if (v == null) continue;
        const ser: Partial<Drawn["series"][number]> = (drawn && drawn.series[s - 1]) || {};
        rows += `<div class="chart-tip-row"><span class="chart-tip-sw" style="background:${ser.color}"></span><span class="chart-tip-lbl">${esc(ser.label as string)}</span><span class="chart-tip-val">${tipVal(v)}</span></div>`;
      }
      if (!rows) { tip.style.display = "none"; return; }
      tip.innerHTML = `<div class="chart-tip-date">${esc(tipDate(idx))}</div>${rows}`;
      tip.style.display = "block";
      const w = tip.offsetWidth, h = tip.offsetHeight;
      let x = left + 16; if (x + w > u.over.clientWidth) x = left - w - 16;
      let y = top + 16; if (y + h > u.over.clientHeight) y = u.over.clientHeight - h - 4;
      if (y < 0) y = 4;
      tip.style.transform = `translate(${x}px, ${y}px)`;
    },
  } };
}

/** Draw lines into `el` and set the visible window. `x` is the axis in display
 * seconds, `newShift` the half period it is shifted by. `prevDates` are the
 * dates the current window was drawn on (null after a dataset switch, so no
 * zoom carries over); `pending` a shared URL's window, applied once. */
export function drawLines(el: HTMLElement, x: number[], lines: Drawn, newShift: number,
  win: { prevDates: string[] | null; range: string | null | undefined; pending: XWindow | null }) {
  shift = newShift;
  drawn = lines;
  const series = lines.series;
  const uData = [x, ...series.map(s => s.values)] as uPlot.AlignedData;
  const prev = plot;
  // Read before setData/destroy: the window the visitor was looking at.
  const prevScale = chartScale();

  // Same set of lines (e.g. a transform toggle)? Reuse the instance — no DOM
  // teardown, no flicker. Otherwise the series config changed, so recreate.
  // prev.root must still live inside the current #chart: a full re-render
  // (renderDetail on a hashchange reopen — e.g. WhatsApp's in-app browser reuses
  // the document and re-navigates instead of reloading) builds a fresh empty
  // #chart and detaches the old canvas, yet the plot still points at it.
  // Reusing it would setData() a DETACHED plot, leaving the visible #chart blank.
  const sameShape = !!prev && !!prev.root && el.contains(prev.root) &&
    prev.series.length - 1 === series.length &&
    series.every((s, i) => prev.series[i + 1]?.label === s.label);

  // Keep the visible window across redraws, including a changed selection: a
  // range preset (5Y, …) is re-applied to the new data, so the window stays
  // what the lit button says; a drag-zoom is kept (lib/xwindow keptZoom); a
  // pending URL window (#8) wins over both.
  const next = windowAfterDraw({ prevScale, prevDates: win.prevDates, dates: lines.dates, shift, range: win.range, pending: win.pending });
  if (sameShape && prev) {
    prev.setData(uData, true);  // resetScales: y re-fits to the new transform
  } else {
    const freq = lines.freq;
    const xTickVals = (freq === "quarterly" || freq === "monthly") ? fmtPeriodTick(freq) : null;   // #14
    const opts: uPlot.Options = {
      width: el.clientWidth || 680, height: CHART_H,
      legend: { show: false },
      cursor: { y: false, points: { size: 6 } },   // vertical crosshair only
      plugins: [tooltipPlugin()],                   // hover box with exact values
      scales: { x: { time: true } },
      // Dates are UTC midnights; uPlot's default would label them in the viewer's
      // zone, one day early west of UTC (daily and weekly ticks).
      tzDate: ts => uPlot.tzDate(new Date(ts * 1000), "Etc/UTC"),
      axes: [
        { stroke: axisStroke, grid: { stroke: gridStroke, width: 1 }, ticks: { stroke: gridStroke, width: 1 }, font: "12px Inter", ...(xTickVals ? { values: xTickVals } : {}) },
        { stroke: axisStroke, grid: { stroke: gridStroke, width: 1 }, ticks: { stroke: gridStroke, width: 1 }, font: "12px Inter", size: 52, values: (u, sp) => sp.map(v => drawn && drawn.pct ? fmtTick(v * 100) + "%" : fmtTick(v)) }
      ],
      hooks: {
        // A drag-zoom is a window of the user's own (main.ts: no preset is lit
        // any more, and the next redraw keeps this window rather than a preset's).
        setSelect: [u => { if (u.select.width > 0) events.onUserZoom(); }],
        // #7: when indexing to the viewport, a drag-zoom must re-base to the new
        // visible start; otherwise the window goes into the URL.
        setScale: [(u, key) => { if (key === "x") events.onXScale(); }],
      },
      // spanGaps: connect across nulls so a lower-frequency line (e.g. quarterly
      // GDP) stays continuous when overlaid on a denser monthly/daily axis.
      series: [{}, ...series.map(s => ({ label: s.label, stroke: s.color, width: 2, spanGaps: true, dash: s.dash || [] }))]
    };
    if (prev) prev.destroy();
    el.innerHTML = "";
    plot = new uPlot(opts, uData, el);
  }
  if (next && plot) plot.setScale("x", next);
}

window.addEventListener("resize", () => {
  const el = document.getElementById("chart");
  if (plot && el) plot.setSize({ width: el.clientWidth, height: CHART_H });
});

// A theme flip is colour-only: the chrome restyles itself from the CSS tokens,
// and the canvas just repaints (axes/gridlines re-read the tokens on each draw).
// No teardown, no refetch, so no flash and no lost zoom.
onThemeChange(() => { if (plot) plot.redraw(); });
